/* The /collections router — filter the shelves, open one in the rail.
 *
 * A hundred lines of its own behaviour over parts that already exist. It is NOT showroom-app.js:
 * that engine is built on products (a cart, variants, prices, thirty-four elements by id) and a
 * collection has none of those. What it borrows is the same GRAMMAR — cards indexed from the DOM
 * once, facets built from the cards with live counts, a control with nothing to match hiding
 * itself, show-and-hide rather than rebuild, and the rail as the place a selection opens without
 * losing your place in the grid.
 *
 * Every card is a real <a href> underneath (sections/collections-router.liquid), so none of this is
 * load-bearing for a crawler or for someone with JavaScript off: the page is a list of links first.
 */
(() => {
  const app = document.getElementById('ckg-router');
  if (!app) return;

  const $ = (id) => document.getElementById(id);
  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
  /* What GA4 gets told — see the note in showroom-app.js. Guarded: the pixel manager may not have
     booted, and a router must never fail to filter because analytics was late. */
  const track = (name, payload) => { try { window.Shopify?.analytics?.publish?.(name, { surface: 'collections', ...payload }); } catch (_) { /* never */ } };
  const money = (n) => (n == null ? '' : '$' + (Math.round(n * 100) % 100 === 0 ? String(Math.round(n)) : n.toFixed(2)));
  const range = (lo, hi) => (lo == null ? '' : lo === hi ? money(lo) : money(lo) + '–' + money(hi));

  /* ---------- the shop's own words for a group ---------- */
  const WALLS = [
    ['tea', 'Tea'],
    ['spice', 'Spices'],
    ['wellness', 'Herbal & Wellness'],
    ['more', 'Teaware & gifts'],
  ];
  const label = (list, v) => (list.find((x) => x[0] === v) || [v, v])[1];

  /* ONE ROW OF CHIPS, NOT TWO (Dayton, 2026-09-20). A second row — by kind, by cuisine, what's
     going on — was a filter bar pretending to be a page over a grid of seventy-two things that a
     reader can simply look at. The four walls are the only question worth asking before the grid
     answers. Every card still carries its `data-kind`, so this comes back as a second entry here
     and nothing else, if it is ever wanted. */
  const FACETS = [['wall', WALLS]];

  /* ---------- read every card once ---------- */
  const CARDS = [...app.querySelectorAll('.ckg-ccard')].map((el) => {
    const d = el.dataset;
    return {
      el,
      h: d.h,
      url: d.url,
      title: d.title || '',
      wall: d.wall || '',
      kind: d.kind || '',
      count: num(d.count) || 0,
      buyable: num(d.buyable) || 0,
      min: num(d.min),
      max: num(d.max),
      sizes: d.sizes || '',
      rating: d.rating || '',
      rc: num(d.rc) || 0,
      head: d.head || '',
      lede: d.lede || '',
      desc: d.desc || '',
      img: d.img || '',
      /* The first few product names stay in the card's data as SEARCH TEXT only — typing
         "lapsang" should find the shelf it is on. The rail's own list is fetched. */
      text: [d.title, d.desc, d.lede, d.top].join(' ').toLowerCase(),
    };
  });

  const S = { wall: new Set(), q: '', sel: null };

  /* Passing the facet being drawn means its own chips are counted against everything ELSE, so
     choosing one value does not blank its siblings — the showroom's rule, and the reason its
     counts read true. It costs nothing with one row and is what makes a second row honest. */
  const matches = (c, except) => {
    if (S.q && !S.q.split(/\s+/).every((w) => c.text.includes(w))) return false;
    for (const [key] of FACETS) {
      if (except !== key && S[key].size && !S[key].has(c[key])) return false;
    }
    return true;
  };

  function facetRow(key, list) {
    const pool = CARDS.filter((c) => matches(c, key));
    const counts = new Map();
    pool.forEach((c) => counts.set(c[key], (counts.get(c[key]) || 0) + 1));
    const vals = list.filter((x) => counts.get(x[0]));
    /* A CONTROL WITH NOTHING TO MATCH HIDES ITSELF, and so does one whose single value every card
       carries — pressing it would change nothing. */
    if (!vals.length || (vals.length === 1 && counts.get(vals[0][0]) === pool.length && !S[key].size)) return '';
    /* No label. With one row of four, "Which wall" was a caption on a thing that says what it is. */
    return `<div class="sr-facet"><div class="sr-chiprow">${vals.map(
      ([v, l]) => `<button class="sr-chip" type="button" data-f="${key}" data-v="${esc(v)}" aria-pressed="${S[key].has(v)}">${
        esc(l)}<span class="n">${counts.get(v)}</span></button>`).join('')}</div></div>`;
  }

  function buildFacets() {
    $('ckg-facets').innerHTML = FACETS.map(([key, list]) => facetRow(key, list)).join('');
    $('ckg-facets').querySelectorAll('.sr-chip').forEach((b) => (b.onclick = () => {
      const set = S[b.dataset.f];
      set.has(b.dataset.v) ? set.delete(b.dataset.v) : set.add(b.dataset.v);
      track('shelf_filter', { facet: b.dataset.f, value: b.dataset.v, on: set.has(b.dataset.v) });
      render();
    }));
  }

  function render() {
    const hits = CARDS.filter((c) => matches(c));
    const keys = new Set(hits.map((c) => c.h));
    CARDS.forEach((c) => { c.el.hidden = !keys.has(c.h); });
    CARDS.forEach((c) => c.el.classList.toggle('is-sel', c.h === S.sel));

    buildFacets();

    const active = FACETS.reduce((n, [key]) => n + S[key].size, 0) + (S.q ? 1 : 0);
    const clear = $('ckg-clear');
    clear.disabled = !active;
    clear.textContent = 'Clear all' + (active ? ` (${active})` : '');

    const bits = FACETS
      .filter(([key]) => S[key].size)
      .map(([key, list]) => [...S[key]].map((v) => label(list, v).toLowerCase()).join(' or '));
    const strip = $('ckg-strip');
    strip.innerHTML = `<span><b>${hits.length}</b> ${hits.length === 1 ? 'collection' : 'collections'}${
      S.q ? ` matching “${esc(S.q)}”` : ''}${bits.length ? ' · ' + esc(bits.join(' · ')) : ''}</span>${
      active ? '<button class="sr-cta" data-ckg-widen type="button">Show everything</button>' : ''}`;

    let empty = $('ckg-empty');
    if (!hits.length) {
      if (!empty) {
        empty = document.createElement('div');
        empty.id = 'ckg-empty';
        empty.className = 'sr-empty';
        $('ckg-grid').appendChild(empty);
      }
      empty.innerHTML = `<p>No collection matches ${S.q ? `“${esc(S.q)}”` : 'those'}.</p>
        <div class="sr-empty__acts"><button class="sr-cta" data-ckg-widen type="button">Show everything</button>
        ${S.q ? `<a class="sr-cta" href="/search?q=${encodeURIComponent(S.q)}">Search the whole shop</a>` : ''}</div>`;
      empty.hidden = false;
    } else if (empty) empty.hidden = true;

    renderRail();
    writeUrl();
  }

  /* ---------- the rail ----------
     The product list is fetched, not carried: `/collections/{handle}?view=ckglist` is an alternate
     template returning that collection's products as JSON (templates/collection.ckglist.liquid).
     Nineteen hundred rows of titles and handles in the page would be a hundred kilobytes for a list
     a reader opens one of — and the shape of the inert-markup mistake this rebuild exists to undo.
     Cached per handle, so opening a shelf twice costs one request. */
  const LISTS = new Map();
  async function loadList(handle) {
    if (LISTS.has(handle)) return LISTS.get(handle);
    /* NO `Accept: application/json` HEADER, and that is the whole trick. Asking a Shopify
       storefront URL for JSON makes it answer with its OWN built-in collection object —
       {"collection":{…}} — and skip the `view=` template entirely, so the alternate template never
       runs and the reply has no products in it at all. The header was added here as hygiene and was
       the one thing that broke this: the list came back an object, `rows.length` was undefined, and
       the guard below blanked the panel without a word. Ask for the page; the template answers. */
    const p = fetch(`/collections/${encodeURIComponent(handle)}?view=ckglist`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
    LISTS.set(handle, p);
    return p;
  }

  function paintList(handle, host) {
    if (!host || S.sel !== handle) return;
    loadList(handle).then((rows) => {
      /* The selection can change while a fetch is in the air; the late answer must not overwrite
         the rail someone is now looking at. */
      if (!host.isConnected || S.sel !== handle) return;
      /* Array.isArray, not a truthiness check on .length: the failure this replaced returned a
         perfectly valid OBJECT, whose missing `length` read as "no products" and emptied the panel
         silently. A shape that is not the shape says so in the console once and leaves the rest of
         the rail alone. */
      if (!Array.isArray(rows)) {
        if (rows) console.warn('[ckg] products feed for %s came back in an unexpected shape', handle);
        host.innerHTML = '';
        return;
      }
      if (!rows.length) { host.innerHTML = ''; return; }
      host.innerHTML = `<h4>Everything on this shelf <span class="ckg-rail__n">${rows.length}</span></h4>
        <ul class="ckg-rail__list">${rows.map((r) => `<li${r.a ? '' : ' class="is-out"'}>
          <a href="/products/${encodeURIComponent(r.h)}"><span>${esc(r.t)}</span>
          <b>${r.a ? esc(r.p) : 'Sold out'}</b></a></li>`).join('')}</ul>`;
    });
  }

  function renderRail() {
    const rail = $('ckg-rail');
    if (!rail) return;
    const c = CARDS.find((x) => x.h === S.sel);
    if (!c) {
      rail.innerHTML = '<div class="sr-rail__empty">Pick any shelf and it opens here — what is on it, what it costs, and everything it holds — without losing your place in the grid.</div>';
      return;
    }
    rail.innerHTML = railHTML(c);
    /* Found inside THIS rail rather than by id: the same fragment can be open in the phone's sheet,
       and an id lookup would find whichever copy came first in the DOM — the hidden one. */
    paintList(c.h, rail.querySelector('.ckg-rail__listwrap'));
  }

  /* The detail fragment, shared by the rail and the phone's sheet so the two cannot drift. */
  function railHTML(c) {
    const figs = [
      [c.count, c.count === 1 ? 'product' : 'products'],
      /* Never state an absence as a finding: a shelf with nothing in stock says nothing about
         stock rather than printing a zero. */
      c.buyable > 0 ? [c.buyable, 'in stock'] : null,
      c.min != null ? [range(c.min, c.max), c.sizes || 'a bag'] : null,
      c.rc > 0 ? [c.rating, `from ${c.rc} review${c.rc === 1 ? '' : 's'}`] : null,
    ].filter(Boolean);

    /* The button sits ABOVE the list: under a hundred and sixty products it would be a scroll
       away from the person who has already decided. */
    return `<div class="ckg-rail">
      ${c.img ? `<img class="sr-rail__img" src="${esc(c.img)}" alt="" loading="lazy">` : ''}
      <div class="sr-rail__b">
        <h3 class="sr-rail__t">${esc(c.title)}</h3>
        ${c.head ? `<p class="sr-rail__sub">${esc(c.head)}</p>` : ''}
        ${c.lede || c.desc ? `<p>${esc(c.lede || c.desc)}</p>` : ''}
        <h4>On the shelf</h4>
        <div class="ckg-rail__figs">${figs.map(([v, l]) => `<div><b>${esc(v)}</b><span>${esc(l)}</span></div>`).join('')}</div>
        <div class="ckg-rail__go"><a class="sr-btn" href="${esc(c.url)}">Open the shelf</a></div>
        <div class="ckg-rail__listwrap"></div>
      </div>
    </div>`;
  }

  /* ---------- the sheet: the rail's job on a phone ----------
     Below 810 the rail is gone, and until 2026-09-20 a tap on a card or on Details left the page
     for the shelf — the one thing the rail exists to avoid. Now it opens the same fragment in the
     Showroom's bottom sheet (assets/showroom-sheet.js), exactly as a product opens there. `sr-rail`
     on the inner box is load-bearing: the fragment's type styles are scoped under it, and
     showroom-mobile.css undoes the rail's own box inside a sheet. */
  let cardSheet = null;
  function openCardSheet(handle) {
    const c = CARDS.find((x) => x.h === handle);
    if (!c) return;
    if (!window.SRSheet) { window.location.href = c.url; return; }
    if (!cardSheet) {
      cardSheet = window.SRSheet.create({
        ariaLabel: 'Collection',
        onClose: () => { S.sel = null; CARDS.forEach((x) => x.el.classList.remove('is-sel')); },
      });
    }
    if (S.sel !== handle) track('rail_open', { handle });
    S.sel = handle;
    CARDS.forEach((x) => x.el.classList.toggle('is-sel', x.h === handle));
    cardSheet.setContent('<div class="sr-sheet__scroll"><div class="sr-rail">' + railHTML(c) + '</div></div>');
    paintList(c.h, cardSheet.body.querySelector('.ckg-rail__listwrap'));
    cardSheet.open();
  }

  /* ---------- url state ----------
     The facets only, and under short names of our own: Shopify reserves several query parameters
     on storefront routes, and the showroom was bitten by two of them. The search box is not in the
     URL, because a half-typed word is not a place anyone meant to link to. */
  const PARAM = { wall: 'w' };
  function writeUrl() {
    const u = new URL(window.location);
    FACETS.forEach(([key]) => (S[key].size
      ? u.searchParams.set(PARAM[key], [...S[key]].join(','))
      : u.searchParams.delete(PARAM[key])));
    /* The old second facet's parameter, cleared off any link still carrying it. */
    u.searchParams.delete('k');
    history.replaceState(null, '', u);
  }
  function readUrl() {
    const u = new URLSearchParams(window.location.search);
    FACETS.forEach(([key]) => (u.get(PARAM[key]) || '').split(',').filter(Boolean).forEach((v) => S[key].add(v)));
  }

  /* ---------- wiring ---------- */
  /* Is the rail actually on screen? Below 810 showroom-mobile.css takes it away, and a control
     that opens something invisible has to do something else instead. */
  const railVisible = () => !!($('ckg-rail') && $('ckg-rail').offsetParent);

  function select(handle) {
    /* Only an OPEN is interest; closing the same card again is not. */
    if (S.sel !== handle) track('rail_open', { handle });
    S.sel = S.sel === handle ? null : handle;
    CARDS.forEach((c) => c.el.classList.toggle('is-sel', c.h === S.sel));
    renderRail();
  }

  app.addEventListener('click', (e) => {
    /* THE CARD BODY OPENS THE RAIL — the Showroom's behaviour, where clicking a card opens it
       beside the grid and only "Full page" leaves. The body is still a real <a href> underneath,
       which is what the crawler follows and what a middle-click or a ctrl-click opens in a new
       tab; those are let through untouched, because taking them over is the one thing a link
       must never do. Where there is no rail, the anchor does exactly what it says. */
    const door = e.target.closest('.ckg-ccard__door');
    if (door && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      const card = door.closest('.ckg-ccard');
      /* With a rail, the card opens beside the grid; without one, in the sheet. Either way the
         reader keeps their place. */
      if (card) (railVisible() ? select : openCardSheet)(card.dataset.h);
      return;
    }

    const open = e.target.closest('[data-ckg-open]');
    if (open) {
      e.preventDefault();
      (railVisible() ? select : openCardSheet)(open.dataset.ckgOpen);
      return;
    }
    if (e.target.closest('[data-ckg-widen]')) {
      reset();
      render();
    }
  });

  /* One place that knows how to empty the panel, so a facet added or removed cannot leave a
     Clear button that half-clears. */
  function reset() {
    FACETS.forEach(([key]) => S[key].clear());
    S.q = '';
  }
  $('ckg-clear').onclick = () => { reset(); render(); };

  /* No search field any more (2026-09-21) — S.q stays empty and the chips do the narrowing; the
     wording that reacted to a typed query is left in place for the day one comes back. */

  readUrl();
  render();
})();
